import React, {FC, ReactNode} from 'react';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {TimelineActivityActionsEnum} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {ActionSkippedBasicActivity} from '../../../../../../srcCommon/inboxes/viewer/activity/visualActivity/visualBasicActivity';
import {SkippedActionReasonsEnum, SkippedActionTypesEnum} from '../../../../../models/api/activityModel';
import {ActivityWithIcon} from '../activityWithIcon/activityWithIcon';
import {RuleActivitySource} from './activitySource';

interface BasicViewerActionSkippedProps {
  panelId: ViewerPanelId;
  visualActivity: ActionSkippedBasicActivity;
}

const skipActionTitles: Record<SkippedActionTypesEnum, ReactNode> = {
  [SkippedActionTypesEnum.ASSIGN]: (
    <Trans id="skipped-action-assign">
      <strong>Skipped</strong> assignment in
    </Trans>
  ),
  [SkippedActionTypesEnum.AI_TAGGING]: (
    <Trans id="skipped-action-ai-tagging">
      <strong>Skipped</strong> tag with AI in
    </Trans>
  ),
  [SkippedActionTypesEnum.AI_DRAFT_REPLY]: (
    <Trans id="skipped-action-ai-draft-reply">
      <strong>Skipped</strong> suggest reply in
    </Trans>
  ),
  [SkippedActionTypesEnum.FALLBACK_UNKNOWN]: (
    <Trans id="skipped-action-unknown">
      <strong>Skipped</strong> action in
    </Trans>
  ),
};

const skipReasonsTooltips: Partial<Record<SkippedActionReasonsEnum, ReactNode>> = {
  [SkippedActionReasonsEnum.UNFIT_MESSAGE]: (
    <Trans id="skipped-action-tooltip-unfit-message">Can only suggest responses to inbound emails.</Trans>
  ),
  [SkippedActionReasonsEnum.UNFIT_KB]: (
    <Trans id="skipped-action-tooltip-unfit-kb">
      Rule must reference an external knowledge base that is published.
    </Trans>
  ),
  [SkippedActionReasonsEnum.NO_QUERY]: (
    <Trans id="skipped-action-tooltip-no-query">
      Did not suggest a response because it looks like the original email does not contain any question.
    </Trans>
  ),
  [SkippedActionReasonsEnum.NO_KB_ARTICLES]: (
    <Trans id="skipped-action-tooltip-no-kb-articles">
      Could not find any relevant article in your knowledge base to support a response.
    </Trans>
  ),
  [SkippedActionReasonsEnum.AI_TAGGING_NOT_EMAIL]: (
    <Trans id="skipped-action-tooltip-ai-tagging-not-email">Can only tag with AI on emails.</Trans>
  ),
  [SkippedActionReasonsEnum.AI_DISABLED]: (
    <Trans id="skipped-action-tooltip-ai-disabled">AI features are disabled for your company.</Trans>
  ),
};

export const BasicViewerActionSkipped: FC<BasicViewerActionSkippedProps> = ({panelId, visualActivity}) => {
  const title = skipActionTitles[visualActivity.skippedActionType];
  const tooltipContent = skipReasonsTooltips[visualActivity.skipReason];

  return (
    <ActivityWithIcon iconName="gearActivity">
      {title}
      <RuleActivitySource
        panelId={panelId}
        activitySource={visualActivity.source}
        timelineActivityAction={TimelineActivityActionsEnum.CLICK_RULE_ACTION_SKIPPED}
      />
      {tooltipContent && <TooltipCoordinator maxWidth={300} render={() => tooltipContent} />}
    </ActivityWithIcon>
  );
};
